<template>
  <div class="system-log">
    <el-card>
      <div slot="header" class="card-header">
        <span>{{ pageTitle }}</span>
        <div>
          <el-button type="info" size="small" icon="el-icon-data-line" @click="showStatistics">
            统计信息
          </el-button>
        </div>
      </div>

      <!-- 搜索区域 -->
      <el-form :inline="true" class="search-form">
        <el-form-item label="操作类型">
          <el-select v-model="searchForm.operationType" placeholder="全部" clearable style="width: 150px">
            <el-option label="新增" value="CREATE"></el-option>
            <el-option label="修改" value="UPDATE"></el-option>
            <el-option label="删除" value="DELETE"></el-option>
            <el-option label="登录" value="LOGIN"></el-option>
            <el-option label="导出" value="EXPORT"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="操作模块">
          <el-select v-model="searchForm.module" placeholder="全部" clearable style="width: 150px">
            <el-option label="资源管理" value="资源管理"></el-option>
            <el-option label="用户管理" value="用户管理"></el-option>
            <el-option label="系统配置" value="系统配置"></el-option>
            <el-option label="权限管理" value="权限管理"></el-option>
            <el-option label="会员管理" value="会员管理"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="操作人">
          <el-input 
            v-model="searchForm.operatorName" 
            placeholder="请输入操作人姓名"
            @keyup.enter.native="handleSearch"
            clearable
            style="width: 200px">
          </el-input>
        </el-form-item>
        <el-form-item label="时间范围">
          <el-date-picker
            v-model="dateRange"
            type="datetimerange"
            range-separator="至"
            start-placeholder="开始时间"
            end-placeholder="结束时间"
            value-format="yyyy-MM-dd HH:mm:ss"
            style="width: 360px">
          </el-date-picker>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" @click="handleSearch">搜索</el-button>
          <el-button icon="el-icon-refresh" @click="resetSearch">重置</el-button>
        </el-form-item>
      </el-form>

      <!-- 表格 -->
      <el-table :data="logList" v-loading="loading" border>
        <el-table-column prop="id" label="ID" width="80"></el-table-column>
        <el-table-column prop="operationType" label="操作类型" width="100">
          <template slot-scope="scope">
            <el-tag v-if="scope.row.operationType === 'CREATE'" type="success">新增</el-tag>
            <el-tag v-else-if="scope.row.operationType === 'UPDATE'" type="primary">修改</el-tag>
            <el-tag v-else-if="scope.row.operationType === 'DELETE'" type="danger">删除</el-tag>
            <el-tag v-else-if="scope.row.operationType === 'LOGIN'" type="info">登录</el-tag>
            <el-tag v-else-if="scope.row.operationType === 'EXPORT'" type="warning">导出</el-tag>
            <el-tag v-else>{{ scope.row.operationType }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="module" label="操作模块" width="120"></el-table-column>
        <el-table-column prop="operationDesc" label="操作描述" min-width="200" show-overflow-tooltip></el-table-column>
        <el-table-column prop="operatorName" label="操作人" width="100"></el-table-column>
        <el-table-column prop="ipAddress" label="IP地址" width="130"></el-table-column>
        <el-table-column prop="status" label="状态" width="80">
          <template slot-scope="scope">
            <el-tag v-if="scope.row.status === 1" type="success" size="small">成功</el-tag>
            <el-tag v-else type="danger" size="small">失败</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="createdAt" label="操作时间" width="160">
          <template slot-scope="scope">
            {{ formatDate(scope.row.createdAt) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="180" fixed="right">
          <template slot-scope="scope">
            <el-button type="text" size="small" @click="handleViewDetail(scope.row)">查看详情</el-button>
            <el-button 
              type="text" 
              size="small" 
              style="color: #f56c6c" 
              @click="handleDelete(scope.row.id)">
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <el-pagination
        @current-change="handlePageChange"
        @size-change="handleSizeChange"
        :current-page="currentPage"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="pageSize"
        layout="total, sizes, prev, pager, next, jumper"
        :total="total"
        style="text-align: center; margin-top: 20px;">
      </el-pagination>
    </el-card>

    <!-- 日志详情对话框 -->
    <el-dialog 
      title="日志详情" 
      :visible.sync="detailVisible" 
      width="900px">
      <div v-if="currentLog" class="log-detail">
        <el-descriptions :column="2" border>
          <el-descriptions-item label="操作ID">{{ currentLog.id }}</el-descriptions-item>
          <el-descriptions-item label="操作类型">
            <el-tag v-if="currentLog.operationType === 'CREATE'" type="success">新增</el-tag>
            <el-tag v-else-if="currentLog.operationType === 'UPDATE'" type="primary">修改</el-tag>
            <el-tag v-else-if="currentLog.operationType === 'DELETE'" type="danger">删除</el-tag>
            <el-tag v-else>{{ currentLog.operationType }}</el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="操作模块">{{ currentLog.module }}</el-descriptions-item>
          <el-descriptions-item label="业务类型">{{ currentLog.businessType || '-' }}</el-descriptions-item>
          <el-descriptions-item label="业务ID">{{ currentLog.businessId || '-' }}</el-descriptions-item>
          <el-descriptions-item label="操作人">{{ currentLog.operatorName }}</el-descriptions-item>
          <el-descriptions-item label="IP地址">{{ currentLog.ipAddress || '-' }}</el-descriptions-item>
          <el-descriptions-item label="操作时间">{{ formatDate(currentLog.createdAt) }}</el-descriptions-item>
          <el-descriptions-item label="操作描述" :span="2">
            {{ currentLog.operationDesc || '-' }}
          </el-descriptions-item>
          <el-descriptions-item label="请求方法">{{ currentLog.requestMethod || '-' }}</el-descriptions-item>
          <el-descriptions-item label="请求URL" :span="1">
            {{ currentLog.requestUrl || '-' }}
          </el-descriptions-item>
        </el-descriptions>

        <!-- 数据对比区域 -->
        <div v-if="currentLog.dataBefore || currentLog.dataAfter" class="data-compare">
          <h3 style="margin-top: 20px; margin-bottom: 10px;">数据变更对比</h3>
          <el-row :gutter="20">
            <el-col :span="12">
              <div class="compare-panel">
                <div class="panel-header">修改前数据</div>
                <div class="panel-content">
                  <pre v-if="currentLog.dataBefore">{{ formatJson(currentLog.dataBefore) }}</pre>
                  <div v-else class="empty-tip">无数据</div>
                </div>
              </div>
            </el-col>
            <el-col :span="12">
              <div class="compare-panel">
                <div class="panel-header">修改后数据</div>
                <div class="panel-content">
                  <pre v-if="currentLog.dataAfter">{{ formatJson(currentLog.dataAfter) }}</pre>
                  <div v-else class="empty-tip">无数据</div>
                </div>
              </div>
            </el-col>
          </el-row>

          <!-- 差异高亮显示 -->
          <div v-if="currentLog.dataBefore && currentLog.dataAfter" style="margin-top: 20px;">
            <h4>字段变更明细</h4>
            <el-table :data="getChangedFields()" border>
              <el-table-column prop="field" label="字段名" width="200"></el-table-column>
              <el-table-column prop="before" label="修改前" min-width="250">
                <template slot-scope="scope">
                  <span class="old-value">{{ scope.row.before }}</span>
                </template>
              </el-table-column>
              <el-table-column prop="after" label="修改后" min-width="250">
                <template slot-scope="scope">
                  <span class="new-value">{{ scope.row.after }}</span>
                </template>
              </el-table-column>
            </el-table>
          </div>
        </div>

        <!-- 错误信息 -->
        <div v-if="currentLog.status === 0 && currentLog.errorMsg" class="error-info">
          <h3>错误信息</h3>
          <el-alert type="error" :closable="false">
            <pre>{{ currentLog.errorMsg }}</pre>
          </el-alert>
        </div>
      </div>
    </el-dialog>

    <!-- 统计信息对话框 -->
    <el-dialog 
      title="操作统计" 
      :visible.sync="statisticsVisible" 
      width="600px">
      <el-descriptions :column="2" border>
        <el-descriptions-item label="总操作数">
          <span style="font-size: 20px; font-weight: bold; color: #409EFF;">
            {{ statistics.totalCount || 0 }}
          </span>
        </el-descriptions-item>
        <el-descriptions-item label="新增操作">
          <el-tag type="success">{{ statistics.createCount || 0 }}</el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="修改操作">
          <el-tag type="primary">{{ statistics.updateCount || 0 }}</el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="删除操作">
          <el-tag type="danger">{{ statistics.deleteCount || 0 }}</el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="登录次数">
          <el-tag type="info">{{ statistics.loginCount || 0 }}</el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="导出次数">
          <el-tag type="warning">{{ statistics.exportCount || 0 }}</el-tag>
        </el-descriptions-item>
      </el-descriptions>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'SystemLog',
  data() {
    return {
      pageTitle: '系统日志',
      searchForm: {
        operationType: '',
        module: '',
        operatorName: ''
      },
      dateRange: null,
      logList: [],
      loading: false,
      currentPage: 1,
      pageSize: 10,
      total: 0,
      detailVisible: false,
      currentLog: null,
      statisticsVisible: false,
      statistics: {}
    }
  },
  mounted() {
    this.loadPageTitle()
    this.loadData()
  },
  methods: {
    async loadPageTitle() {
      try {
        const menuData = localStorage.getItem('admin_menu_data')
        if (menuData) {
          const menus = JSON.parse(menuData)
          const allMenus = menus.menus || []
          const currentMenu = allMenus.find(m => m.url === this.$route.path)
          if (currentMenu) {
            this.pageTitle = currentMenu.name
          }
        }
      } catch (error) {
        console.error('加载页面标题失败:', error)
      }
    },
    async loadData() {
      this.loading = true
      try {
        const params = {
          current: this.currentPage,
          size: this.pageSize,
          operationType: this.searchForm.operationType || null,
          module: this.searchForm.module || null,
          operatorName: this.searchForm.operatorName || null
        }
        
        if (this.dateRange && this.dateRange.length === 2) {
          params.startTime = this.dateRange[0]
          params.endTime = this.dateRange[1]
        }
        
        const res = await this.$axios.get('/admin/logs', { params })
        if (res.data.code === 200) {
          this.logList = res.data.data.records
          this.total = res.data.data.total
        }
      } catch (error) {
        console.error('加载日志失败:', error)
        this.$message.error('加载日志失败')
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.currentPage = 1
      this.loadData()
    },
    resetSearch() {
      this.searchForm = {
        operationType: '',
        module: '',
        operatorName: ''
      }
      this.dateRange = null
      this.currentPage = 1
      this.loadData()
    },
    handlePageChange(page) {
      this.currentPage = page
      this.loadData()
    },
    handleSizeChange(size) {
      this.pageSize = size
      this.loadData()
    },
    async handleViewDetail(row) {
      try {
        const res = await this.$axios.get(`/admin/logs/${row.id}`)
        if (res.data.code === 200) {
          this.currentLog = res.data.data
          this.detailVisible = true
        }
      } catch (error) {
        console.error('加载日志详情失败:', error)
        this.$message.error('加载日志详情失败')
      }
    },
    handleDelete(id) {
      this.$confirm('确定要删除这条日志吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const res = await this.$axios.delete(`/admin/logs/${id}`)
          if (res.data.code === 200) {
            this.$message.success('删除成功')
            this.loadData()
          }
        } catch (error) {
          console.error('删除失败:', error)
          this.$message.error('删除失败')
        }
      }).catch(() => {})
    },
    async showStatistics() {
      try {
        const res = await this.$axios.get('/admin/logs/statistics')
        if (res.data.code === 200) {
          this.statistics = res.data.data
          this.statisticsVisible = true
        }
      } catch (error) {
        console.error('加载统计信息失败:', error)
        this.$message.error('加载统计信息失败')
      }
    },
    formatJson(jsonStr) {
      try {
        const obj = JSON.parse(jsonStr)
        return JSON.stringify(obj, null, 2)
      } catch (e) {
        return jsonStr
      }
    },
    getChangedFields() {
      if (!this.currentLog || !this.currentLog.dataBefore || !this.currentLog.dataAfter) {
        return []
      }
      
      try {
        const before = JSON.parse(this.currentLog.dataBefore)
        const after = JSON.parse(this.currentLog.dataAfter)
        const changes = []
        
        // 获取所有字段
        const allKeys = new Set([...Object.keys(before), ...Object.keys(after)])
        
        allKeys.forEach(key => {
          const beforeValue = before[key]
          const afterValue = after[key]
          
          // 只显示有变化的字段
          if (JSON.stringify(beforeValue) !== JSON.stringify(afterValue)) {
            changes.push({
              field: key,
              before: beforeValue !== undefined ? JSON.stringify(beforeValue) : '-',
              after: afterValue !== undefined ? JSON.stringify(afterValue) : '-'
            })
          }
        })
        
        return changes
      } catch (e) {
        console.error('解析数据失败:', e)
        return []
      }
    },
    formatDate(date) {
      if (!date) return '-'
      
      // 处理ISO 8601格式的日期字符串
      const d = new Date(date)
      
      const year = d.getFullYear()
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      const hours = String(d.getHours()).padStart(2, '0')
      const minutes = String(d.getMinutes()).padStart(2, '0')
      const seconds = String(d.getSeconds()).padStart(2, '0')
      
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
    }
  }
}
</script>

<style scoped>
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.search-form {
  margin-bottom: 20px;
}

.log-detail {
  max-height: 600px;
  overflow-y: auto;
}

.data-compare {
  margin-top: 20px;
}

.compare-panel {
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  overflow: hidden;
}

.panel-header {
  background-color: #f5f7fa;
  padding: 10px 15px;
  font-weight: bold;
  border-bottom: 1px solid #dcdfe6;
}

.panel-content {
  padding: 15px;
  background-color: #fafafa;
  max-height: 300px;
  overflow-y: auto;
}

.panel-content pre {
  margin: 0;
  font-size: 12px;
  line-height: 1.6;
  color: #333;
  white-space: pre-wrap;
  word-wrap: break-word;
}

.empty-tip {
  color: #909399;
  text-align: center;
  padding: 20px;
}

.old-value {
  color: #f56c6c;
  background-color: #fef0f0;
  padding: 2px 8px;
  border-radius: 3px;
  display: inline-block;
}

.new-value {
  color: #67c23a;
  background-color: #f0f9ff;
  padding: 2px 8px;
  border-radius: 3px;
  display: inline-block;
}

.error-info {
  margin-top: 20px;
}

.error-info pre {
  margin: 0;
  font-size: 12px;
  line-height: 1.6;
}
</style>
